/**
 * Utility functions for passenger dietary restriction parsing, severity scoring,
 * iconography, badge styling, and tooltip generation in Cuadro Picnic.
 */

import {
  FOOD_RESTRICTION_LABEL_LOOKUP,
  NON_RESTRICTION_KEYWORDS_SET,
  PassengerDietaryRestrictionDetail,
} from '../models/cuadro-picnic.model';

/**
 * Filters a list of dietary restrictions returning only valid, meaningful passenger restriction entries.
 * Removes empty entries, generic placeholders, and terms matching NON_RESTRICTION_KEYWORDS_SET.
 *
 * @param rawRestrictionsList - Collection of raw dietary restriction strings.
 * @returns Sanitized array containing only active dietary restrictions.
 */
export function filterValidDietaryRestrictions(
  rawRestrictionsList?: string[] | null,
): string[] {
  if (!rawRestrictionsList || rawRestrictionsList.length === 0) {
    return [];
  }

  return rawRestrictionsList.filter((restrictionString) => {
    if (!restrictionString) {
      return false;
    }
    const lowercased = restrictionString.toLowerCase().trim();
    if (!lowercased) {
      return false;
    }

    if (lowercased.includes(':')) {
      const parts = lowercased.split(':');
      const restrictionPart = (parts[1] || '').trim();
      return (
        restrictionPart.length > 0 &&
        !NON_RESTRICTION_KEYWORDS_SET.has(restrictionPart)
      );
    }
    return !NON_RESTRICTION_KEYWORDS_SET.has(lowercased);
  });
}

/**
 * Checks whether a collection contains at least one active, valid dietary restriction.
 *
 * @param rawRestrictionsList - Collection of raw dietary restriction strings.
 * @returns True if valid restrictions exist, false otherwise.
 */
export function hasValidDietaryRestrictions(
  rawRestrictionsList?: string[] | null,
): boolean {
  return filterValidDietaryRestrictions(rawRestrictionsList).length > 0;
}

/**
 * Extracts unique, distinct dietary restriction tag labels across all passengers in a row.
 * Normalizes tokens via FOOD_RESTRICTION_LABEL_LOOKUP and discards non-restrictions.
 *
 * @param rawRestrictionsList - Raw restriction strings from backend.
 * @returns Distinct list of clean restriction tag names (e.g. ['Vegetariano', 'Celiaco/Gluten free']).
 */
export function getDistinctDietaryRestrictionTags(
  rawRestrictionsList?: string[] | null,
): string[] {
  const validPassengerLines =
    filterValidDietaryRestrictions(rawRestrictionsList);
  if (validPassengerLines.length === 0) {
    return [];
  }

  const distinctTagsSet = new Set<string>();

  for (const passengerLine of validPassengerLines) {
    let restrictionsSubstring = passengerLine;
    if (passengerLine.includes(':')) {
      const parts = passengerLine.split(':');
      restrictionsSubstring = parts.slice(1).join(':').trim();
    }

    const individualTokens = restrictionsSubstring.split(/[,;\n]+/);
    for (const tokenItem of individualTokens) {
      const cleanedToken = tokenItem.trim().replace(/^["']|["']$/g, '');
      if (!cleanedToken) continue;

      const lowerKey = cleanedToken.toLowerCase().trim();
      if (NON_RESTRICTION_KEYWORDS_SET.has(lowerKey)) {
        continue;
      }

      if (FOOD_RESTRICTION_LABEL_LOOKUP[lowerKey]) {
        distinctTagsSet.add(FOOD_RESTRICTION_LABEL_LOOKUP[lowerKey]);
      } else {
        const capitalized =
          cleanedToken.charAt(0).toUpperCase() + cleanedToken.slice(1);
        distinctTagsSet.add(capitalized);
      }
    }
  }

  return Array.from(distinctTagsSet);
}

/**
 * Computes a severity numerical score for passenger dietary restrictions to prioritize critical allergies and regimes.
 *
 * @param passengerDetail - Structured passenger dietary restriction detail.
 * @returns Cumulative numerical severity score.
 */
export function calculateRestrictionSeverityScore(
  passengerDetail: PassengerDietaryRestrictionDetail,
): number {
  let totalSeverityScore = 0;
  if (passengerDetail.hasAllergiesOrCritical) {
    totalSeverityScore += 100;
  }

  for (const restrictionItem of passengerDetail.restrictions) {
    const lowerToken = restrictionItem.toLowerCase();
    if (
      lowerToken.includes('alerg') ||
      lowerToken.includes('celiac') ||
      lowerToken.includes('gluten')
    ) {
      totalSeverityScore += 50;
    } else if (
      lowerToken.includes('vegano') ||
      lowerToken.includes('crudi') ||
      lowerToken.includes('lacteo') ||
      lowerToken.includes('lactosa')
    ) {
      totalSeverityScore += 25;
    } else if (
      lowerToken.includes('vegetariano') ||
      lowerToken.includes('ovo') ||
      lowerToken.includes('pesque') ||
      lowerToken.includes('pesce') ||
      lowerToken.includes('paleo')
    ) {
      totalSeverityScore += 10;
    } else {
      totalSeverityScore += 5;
    }
  }

  return totalSeverityScore;
}

/**
 * Parses raw dietary restrictions into structured passenger records and sorts them by restriction severity.
 *
 * @param rawRestrictionsList - Collection of raw dietary restriction strings.
 * @returns Array of structured passenger restriction records sorted by severity descending.
 */
export function parsePassengerDietaryRestrictions(
  rawRestrictionsList?: string[] | null,
): PassengerDietaryRestrictionDetail[] {
  const validPassengerLines =
    filterValidDietaryRestrictions(rawRestrictionsList);
  if (validPassengerLines.length === 0) {
    return [];
  }

  const parsedRecords = validPassengerLines.map((passengerLine) => {
    let passengerName = 'Pasajero';
    let passengerType: 'PAX' | 'TC' = 'PAX';
    let rawRestrictionsSubstring = passengerLine;

    if (passengerLine.includes(':')) {
      const colonIndex = passengerLine.indexOf(':');
      const headerPart = passengerLine.slice(0, colonIndex).trim();
      rawRestrictionsSubstring = passengerLine.slice(colonIndex + 1).trim();

      if (headerPart.toUpperCase().includes('(TC)')) {
        passengerType = 'TC';
        passengerName = headerPart.replace(/\(TC\)/i, '').trim();
      } else if (headerPart.toUpperCase().includes('(PAX)')) {
        passengerType = 'PAX';
        passengerName = headerPart.replace(/\(PAX\)/i, '').trim();
      } else {
        passengerName = headerPart;
      }
    }

    const individualTokens = rawRestrictionsSubstring.split(/[,;\n]+/);
    const parsedRestrictionsList: string[] = [];

    for (const tokenItem of individualTokens) {
      const cleanedToken = tokenItem.trim().replace(/^["']|["']$/g, '');
      if (!cleanedToken) continue;

      const lowerKey = cleanedToken.toLowerCase().trim();
      if (NON_RESTRICTION_KEYWORDS_SET.has(lowerKey)) {
        continue;
      }

      if (FOOD_RESTRICTION_LABEL_LOOKUP[lowerKey]) {
        parsedRestrictionsList.push(FOOD_RESTRICTION_LABEL_LOOKUP[lowerKey]);
      } else {
        const capitalized =
          cleanedToken.charAt(0).toUpperCase() + cleanedToken.slice(1);
        parsedRestrictionsList.push(capitalized);
      }
    }

    const hasAllergiesOrCritical = parsedRestrictionsList.some(
      (restrictionTag) => {
        const lowerTag = restrictionTag.toLowerCase();
        return (
          lowerTag.includes('alerg') ||
          lowerTag.includes('celiac') ||
          lowerTag.includes('gluten')
        );
      },
    );

    return {
      passengerName,
      passengerType,
      restrictions: parsedRestrictionsList,
      rawSummary: passengerLine,
      hasAllergiesOrCritical,
    };
  });

  return parsedRecords.sort((passengerA, passengerB) => {
    const scoreA = calculateRestrictionSeverityScore(passengerA);
    const scoreB = calculateRestrictionSeverityScore(passengerB);
    if (scoreB !== scoreA) {
      return scoreB - scoreA;
    }
    return passengerA.passengerName.localeCompare(passengerB.passengerName);
  });
}

/**
 * Resolves the CSS modifier class for a specific restriction badge.
 *
 * @param restrictionTag - Formatted restriction label.
 * @returns CSS modifier class name.
 */
export function getDietaryBadgeClass(restrictionTag: string): string {
  if (!restrictionTag) return 'restriction-badge--custom';
  const lower = restrictionTag
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

  if (lower.includes('alerg')) {
    return 'restriction-badge--allergy';
  }
  if (lower.includes('celiac') || lower.includes('gluten')) {
    return 'restriction-badge--celiac';
  }
  if (lower.includes('vegano') || lower.includes('crudi-vegano')) {
    return 'restriction-badge--vegan';
  }
  if (
    lower.includes('vegetariano') ||
    lower.includes('lacto-vegetariano') ||
    lower.includes('ovo')
  ) {
    return 'restriction-badge--vegetarian';
  }
  if (lower.includes('lacteo') || lower.includes('lactosa')) {
    return 'restriction-badge--dairy-free';
  }
  if (lower.includes('pesque') || lower.includes('pesce')) {
    return 'restriction-badge--pescatarian';
  }
  if (lower.includes('paleo')) {
    return 'restriction-badge--paleo';
  }
  return 'restriction-badge--custom';
}

/**
 * Resolves the Tabler icon name appropriate for a specific restriction badge.
 *
 * @param restrictionTag - Formatted restriction label.
 * @returns Tabler icon identifier string.
 */
export function getDietaryBadgeIcon(restrictionTag: string): string {
  if (!restrictionTag) return 'alert-circle';
  const lower = restrictionTag
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

  if (
    lower.includes('alerg') ||
    lower.includes('celiac') ||
    lower.includes('gluten')
  ) {
    return 'alert-triangle';
  }
  if (
    lower.includes('vegano') ||
    lower.includes('vegetariano') ||
    lower.includes('crudi') ||
    lower.includes('ovo')
  ) {
    return 'leaf';
  }
  if (lower.includes('lacteo') || lower.includes('lactosa')) {
    return 'milk-off';
  }
  if (lower.includes('pesque') || lower.includes('pesce')) {
    return 'fish';
  }
  if (lower.includes('paleo')) {
    return 'meat';
  }
  return 'shield-alert';
}

/**
 * Determines whether a passenger restriction string contains critical conditions (allergies or celiac/gluten-free).
 *
 * @param rawPassengerLine - Formatted passenger restriction line.
 * @returns True if critical allergy or celiac condition exists, false otherwise.
 */
export function isCriticalDietaryRestriction(
  rawPassengerLine: string | null | undefined,
): boolean {
  if (!rawPassengerLine) return false;
  const normalized = rawPassengerLine
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  return (
    normalized.includes('alerg') ||
    normalized.includes('celiac') ||
    normalized.includes('gluten')
  );
}

/**
 * Formats a clean, multi-line tooltip string presenting all active dietary restrictions for a group.
 *
 * @param rawRestrictionsList - Collection of raw dietary restriction strings.
 * @returns Formatted multi-line string for matTooltip.
 */
export function formatDietaryRestrictionsTooltipText(
  rawRestrictionsList?: string[] | null,
): string {
  const validRestrictions = filterValidDietaryRestrictions(rawRestrictionsList);
  if (validRestrictions.length === 0) {
    return 'Sin restricciones alimentarias registradas';
  }
  const formattedLines = validRestrictions.map(
    (restrictionItem, indexNumber) => `${indexNumber + 1}. ${restrictionItem}`,
  );
  return `Restricciones Alimentarias (${validRestrictions.length} pasajero${validRestrictions.length > 1 ? 's' : ''}):\n${formattedLines.join('\n')}`;
}

/**
 * Extracts the passenger name portion from a formatted passenger restriction string.
 *
 * @param rawPassengerLine - Formatted passenger restriction line.
 * @returns Passenger name prefix string.
 */
export function extractPassengerNamePrefix(rawPassengerLine: string): string {
  if (!rawPassengerLine) return 'Pasajero';
  if (rawPassengerLine.includes(':')) {
    return rawPassengerLine.split(':')[0].trim();
  }
  return 'Pasajero';
}

/**
 * Extracts the dietary restrictions text portion from a formatted passenger restriction string.
 *
 * @param rawPassengerLine - Formatted passenger restriction line.
 * @returns Dietary restrictions text.
 */
export function extractPassengerRestrictionsBody(
  rawPassengerLine: string,
): string {
  if (!rawPassengerLine) return '';
  if (rawPassengerLine.includes(':')) {
    const parts = rawPassengerLine.split(':');
    return parts.slice(1).join(':').trim();
  }
  return rawPassengerLine.trim();
}
